// Derivations shared by the consumables screen.
//
// Stock lives in two stores, because a type is held one of two ways and that is a
// setting on the type itself (see Settings > Consumables):
//
//   tracked types    one row per unit in st.consumableStock, each with its own QR
//                    code, so a unit moves between sites on its own
//   untracked types  a count per location in st.consumableQty[locationName][typeId]
//
// Everything below folds the two into one shape so the panel does not care which
// kind it is looking at.

// The warehouse is a location like any other in the UI, but it is where stock with
// no site sits, so it needs a stable name that cannot collide with a real site.
export const WAREHOUSE = "Warehouse";

// Reorder points are fleet-wide, not per location, so a type is short when its
// total across every site drops below the line — not when one site runs down.
export const TONE_OK = "ok";
export const TONE_LOW = "low";
export const TONE_SHORT = "short";

// A type sits in one of three states against its reorder point. A reorder point of
// 0 means "never warn", so those types are always OK however low they run.
export const toneFor = (total, reorderPoint) => {
  if (!reorderPoint || reorderPoint <= 0) return TONE_OK;
  if (total < reorderPoint) return TONE_SHORT;
  if (total < reorderPoint * 1.5) return TONE_LOW;
  return TONE_OK;
};

export const TONE_LABEL = { [TONE_OK]: "OK", [TONE_LOW]: "Low", [TONE_SHORT]: "Short" };
export const TONE_PILL = {
  [TONE_OK]: "pill pill-success",
  [TONE_LOW]: "pill pill-warning",
  [TONE_SHORT]: "pill pill-danger",
};

// Counts held per location for one type, as { locationName: count }, merging the
// per-unit rows and the per-location quantities.
export const holdingsByLocation = (type, consumableStock = [], consumableQty = {}) => {
  const out = {};
  if (type.tracked) {
    (consumableStock || []).forEach(e => {
      if (e.typeId !== type.id) return;
      if (e.status === "retired") return;
      // A unit with no location is sitting in the warehouse rather than on a site.
      const key = e.locationName || WAREHOUSE;
      out[key] = (out[key] || 0) + 1;
    });
  } else {
    Object.entries(consumableQty || {}).forEach(([locName, byType]) => {
      const n = (byType || {})[type.id] || 0;
      if (n > 0) out[locName] = (out[locName] || 0) + n;
    });
  }
  return out;
};

// One row per consumable type, with its total, its split by location and its state
// against the reorder line. This is what the stock panel renders.
export const stockRows = (consumables = [], consumableStock = [], consumableQty = {}) =>
  (consumables || []).map(type => {
    const byLocation = holdingsByLocation(type, consumableStock, consumableQty);
    const total = Object.values(byLocation).reduce((a, n) => a + n, 0);
    const reorderPoint = type.reorderPoint || 0;
    const tone = toneFor(total, reorderPoint);
    return {
      id: type.id,
      name: type.name,
      tracked: !!type.tracked,
      total,
      reorderPoint,
      byLocation,
      tone,
      // Signed distance from the line: negative is a shortfall.
      gap: reorderPoint > 0 ? total - reorderPoint : null,
    };
  });

// Every location that currently holds something, plus every location explicitly set
// up in settings, so a newly added location shows before any stock reaches it.
export const allLocationNames = (rows = [], consumableLocations = []) => {
  const seen = [];
  (consumableLocations || []).forEach(n => { if (n && !seen.includes(n)) seen.push(n); });
  rows.forEach(r => Object.keys(r.byLocation).forEach(n => { if (!seen.includes(n)) seen.push(n); }));
  return seen;
};

// Rows at or below their reorder line, worst shortfall first, for the alert strip.
// Only a row with a reorder point can be short, so gap is always a number here; the
// fallback keeps the sort total if that ever stops being true.
export const lowRows = (rows = []) =>
  rows
    .filter(r => r.tone === TONE_SHORT || r.tone === TONE_LOW)
    .sort((a, b) => (a.gap ?? Infinity) - (b.gap ?? Infinity));

// Each bar is scaled against its own reorder point rather than against a single
// fleet-wide maximum.
//
// A shared scale sounds fairer but reads as a lie: with 1,204 charging cables setting
// the width, an antenna sitting 16 below its line of 120 fills most of the track and
// looks comfortable, while its reorder tick is squashed down at 10%. The question a
// row answers is "is this type above or below ITS line", not "does this type have
// more units than an unrelated one" — comparing a cable count to an antenna count
// means nothing anyway, since they are consumed at completely different rates.
//
// So the reorder point sits at a fixed position on every track and the fill moves
// against it. A row at exactly its line fills to the tick; below it falls short of
// the tick, above it runs past. That comparison holds across rows even though the
// underlying numbers do not.
export const REORDER_MARK = 0.6;

// The scale that puts this row's reorder point at REORDER_MARK. A type with no
// reorder point has no line to measure against, so it is scaled to its own total and
// the bar is not drawn.
export const rowScale = row => {
  const rp = row.reorderPoint || 0;
  if (rp <= 0) return Math.max(1, row.total);
  const scale = rp / REORDER_MARK;
  // A holding far above the line would still overflow, so the track grows to fit it
  // and the tick slides left — the row reads as "well above" rather than clipping.
  return Math.max(scale, row.total);
};

export const pct = (n, scale) => Math.min(100, Math.round((n / Math.max(1, scale)) * 100)) + "%";

// Units of a tracked type that are in the warehouse and so available to dispatch.
export const availableUnits = (typeId, consumableStock = []) =>
  (consumableStock || []).filter(
    e => e.typeId === typeId && e.status === "in_stock" && !e.locationName
  );

// QR codes already spoken for, so a mis-scan during a setup run cannot overwrite a
// unit that already exists.
export const codesInUse = (consumableStock = []) => {
  const set = new Set();
  (consumableStock || []).forEach(e => {
    if (e.qrCode && e.status !== "retired") set.add(e.qrCode);
  });
  return set;
};
